/**
 * @fileoverview Locale METADATA lookups — the two questions every consumer of
 * `SUPPORTED_LANGUAGES` was previously answering by hand.
 *
 * AAS-I18N-002 ruling 11 (2026-08-10).
 *
 *   `directionForLanguage(code)` — the writing direction `<html dir>` must
 *   carry for a resolved language. Before this existed, NOTHING in the
 *   ecosystem ever wrote `dir`: `useSyncHtmlLang`'s own docblock said so, and
 *   page-shell's RTL bundle documented a workflow where a DEVELOPER typed
 *   `document.dir = 'rtl'` into a console. Direction is a property of the
 *   language, so it is resolved from the language — by the same hook that
 *   mirrors `lang`, so there is exactly ONE writer of the attribute
 *   `useIsRtl` observes.
 *
 *   `isAdvertisedLanguage(code)` — whether a code may be OFFERED to a user.
 *   A pseudo-locale is resolvable but unadvertised; the distinction is what
 *   lets QA select it through the real chain without it appearing in a
 *   customer's dropdown, and without a single `NODE_ENV` branch.
 *
 * Both are total functions: an unknown code is not an error, it is simply
 * neither RTL nor advertised. That matters because a stale storage value or a
 * JWT claim from an older build can name a code this build has never heard
 * of, and a throw there would take down boot.
 *
 * @module @puffin-core/i18n-bootstrap/localeMetadata
 */

import { SUPPORTED_LANGUAGES } from './constants.js';

/** Fallback direction for an unknown code. Also the direction of every advertised locale today. */
const DEFAULT_DIRECTION = 'ltr';

const ENTRY_BY_CODE = new Map(SUPPORTED_LANGUAGES.map((entry) => [entry.code, entry]));

/**
 * The writing direction for a language code.
 *
 * @param {string} [code] - A resolved i18next language (`i18n.resolvedLanguage`).
 * @returns {'ltr'|'rtl'} `'ltr'` for any unknown or non-string code.
 */
export function directionForLanguage(code) {
  const entry = typeof code === 'string' ? ENTRY_BY_CODE.get(code) : undefined;
  return entry ? entry.dir : DEFAULT_DIRECTION;
}

/**
 * May this code be OFFERED to a user (picker, marketing switcher, docs)?
 *
 * An unknown code answers `false`: something the SSOT does not describe must
 * never be advertised on the strength of not being listed as hidden.
 *
 * @param {string} [code]
 * @returns {boolean}
 */
export function isAdvertisedLanguage(code) {
  const entry = typeof code === 'string' ? ENTRY_BY_CODE.get(code) : undefined;
  return Boolean(entry && entry.advertised);
}
